import React, { useEffect, useState, useCallback } from 'react';
import { auditLogApi } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

// Bewusst KEIN Passwort-Feld: Fehlgeschlagene Anmeldeversuche enthalten oft
// echte (nur vertippte) Passwörter der Nutzer. Diese im Klartext zu
// protokollieren wäre ein erhebliches Sicherheits- und Datenschutzrisiko
// (u.a. DSGVO Art. 32) und widerspricht der Projektvorgabe zur
// Datensicherheit — deshalb wird hier ausschließlich E-Mail, Zeitpunkt,
// IP-Adresse und (bei Erfolg) der zugeordnete Benutzer protokolliert.
export default function AdminLoginAudit() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(''); // '' | 'login_success' | 'login_failed'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page };
    if (search.trim()) params.q = search.trim();
    if (status) params.action = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    auditLogApi.list(params).then(({ data }) => {
      setLogs(data.results || data);
      setTotal(data.count ?? (data.results || data).length);
    }).finally(() => setLoading(false));
  }, [search, status, dateFrom, dateTo, page]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setSearch(''); setStatus(''); setDateFrom(''); setDateTo(''); setPage(1);
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Anmeldeprotokoll</h1>
      <p className="text-sm text-gray-500 mb-6">
        Erfolgreiche und fehlgeschlagene Anmeldeversuche, kundenübergreifend. Aus
        Sicherheitsgründen werden bei Fehlversuchen keine Passwörter gespeichert.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          type="text" placeholder="Suche nach E-Mail..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
          <option value="">Alle Status</option>
          <option value="login_success">Nur Erfolgreiche</option>
          <option value="login_failed">Nur Fehlgeschlagene</option>
          <option value="login_blocked">Nur Gesperrt (IP-Sperre)</option>
        </select>
        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          <span>Von</span>
          <input type="date" value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          <span>Bis</span>
          <input type="date" value={dateTo}
            onChange={e => { setDateTo(e.target.value); setPage(1); }}
            className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
        </div>
        {(search || status || dateFrom || dateTo) && (
          <button onClick={resetFilters}
            className="px-3 py-2 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 whitespace-nowrap">
            ✕ Filter zurücksetzen
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        {loading ? <LoadingSpinner /> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
              <tr>
                {['Zeitpunkt', 'E-Mail', 'Status', 'Benutzer', 'Kunde', 'IP-Adresse'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {logs.map(l => (
                <tr key={l.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleString('de-DE')}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-700">{l.details?.email || l.user_email || '–'}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      l.action === 'login_success' ? 'bg-green-100 text-green-700' :
                      l.action === 'login_blocked' ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {l.action === 'login_success' ? 'Erfolgreich' :
                        l.action === 'login_blocked' ? 'Gesperrt (IP)' : 'Fehlgeschlagen'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{l.user_email || '–'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{l.customer_name || '–'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-400 font-mono">{l.ip_address || '–'}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Keine Einträge gefunden.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-1 py-3 flex items-center justify-between text-sm text-gray-500">
        <span>{total} Eintrag{total !== 1 ? 'e' : ''}</span>
        <div className="flex gap-2">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50">
            ← Zurück
          </button>
          <span className="px-3 py-1">Seite {page}</span>
          <button disabled={logs.length < 50} onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50">
            Weiter →
          </button>
        </div>
      </div>
    </div>
  );
}
